import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  MapPin,
  Calendar,
  Tag,
  Quote,
  Activity as ActivityIcon,
  ChevronDown,
} from "lucide-react";
import { Reveal, MaskedLines } from "@/components/Reveal";
import { ACTIVITIES } from "@/data/site";

export default function ActivityDetail() {
  const { slug } = useParams();
  const index = ACTIVITIES.findIndex((a) => a.slug === slug);
  const activity = ACTIVITIES[index];

  // Auto scroll to top when switching between activities
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [slug]);

  // 404 Guard Clause
  if (!activity) {
    return (
      <div
        data-testid="activity-notfound"
        className="max-w-7xl mx-auto px-6 md:px-12 pt-48 pb-40 text-center"
      >
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-michma mb-4 font-extrabold">
          404 Error
        </p>
        <h1 className="font-heading font-extrabold text-4xl md:text-6xl tracking-tighter text-michma-ink">
          Activity not found.
        </h1>
        <Link
          to="/activity"
          data-testid="back-to-activities-404"
          className="inline-flex items-center gap-2 mt-8 bg-michma text-white rounded-full px-8 py-4 font-semibold hover:bg-michma-hover transition-colors shadow-md"
        >
          <ArrowLeft className="w-4 h-4" /> Back to all activities
        </Link>
      </div>
    );
  }

  // Circular navigation for previous/next activities
  const prev = ACTIVITIES[(index - 1 + ACTIVITIES.length) % ACTIVITIES.length];
  const next = ACTIVITIES[(index + 1) % ACTIVITIES.length];

  // Safely normalise description whether provided as a String or an Array
  const rawDescription = activity.description || activity.details || activity.bio;
  const descriptionParagraphs = Array.isArray(rawDescription)
    ? rawDescription
    : typeof rawDescription === "string"
    ? rawDescription.split("\n\n").filter(Boolean)
    : [];

  const highlights = activity.highlights || activity.tags || activity.focus || [];

  // Use 'collageImage' if you create one in site.js, otherwise fallback to the primary 'image'
  const collageSrc = activity.collageImage || activity.images;

  return (
    <div data-testid="activity-detail-page" className="bg-transparent min-h-screen">
      {/* 1. Full-Screen Photo Collage Section */}
      <section className="relative h-screen w-full bg-transparent flex items-center justify-center overflow-hidden">
        {collageSrc ? (
          <motion.img
            initial={{ scale: 1.05, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 1, ease: [0.76, 0, 0.24, 1] }}
            src={collageSrc}
            alt={`${activity.title || activity.name} Collage`}
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400 gap-3">
            <ActivityIcon className="w-16 h-16 stroke-1" />
            <span className="font-mono text-xs uppercase tracking-widest text-white">
              MICHMA Activity
            </span>
          </div>
        )}

        {/* Subtle dark gradient overlay at the bottom so the scroll indicator pops out */}
        <div className="absolute inset-x-0 bottom-0 h-32 bg-transparent from-slate-950/80 to-transparent pointer-events-none" />

        {/* Floating Back Button & Scroll Down Indicator */}
        <div className="absolute top-8 left-6 md:left-12 z-10">
          <Link
            to="/activity"
            data-testid="back-to-activities"
            className="inline-flex items-center gap-2 font-medium text-white hover:text-cyan-300 transition-colors bg-black/40 backdrop-blur-md px-5 py-2.5 rounded-full border border-white/20 shadow-lg"
          >
            <ArrowLeft className="w-4 h-4" /> Back to all activities
          </Link>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-white/80 font-mono text-xs tracking-widest uppercase animate-bounce pointer-events-none">
          <span>Scroll to read details</span>
          <ChevronDown className="w-4 h-4" />
        </div>
      </section>

      {/* 2. Detailed Info Section (Appears when scrolling down) */}
      <section className="max-w-4xl mx-auto px-6 md:px-12 py-24 md:py-32">
        {/* Category Badge */}
        <div className="text-center md:text-left">
          <span className="inline-block font-mono text-xs uppercase tracking-[0.25em] text-michma font-extrabold mb-4 px-4 py-1.5 rounded-full bg-michma-soft border border-michma/20">
            {activity.category || activity.type || "Initiative"}
          </span>

          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05] text-michma-ink">
            {activity.title || activity.name}
          </h1>
        </div>

        {/* Date & Location Badges */}
        {(activity.date || activity.location) && (
          <Reveal delay={0.2}>
            <div className="mt-8 flex flex-wrap items-center justify-center md:justify-start gap-4 text-slate-600 font-medium text-sm">
              {activity.date && (
                <span className="inline-flex items-center gap-2 bg-white px-5 py-2.5 rounded-full border border-slate-200 shadow-xs">
                  <Calendar className="w-4 h-4 text-michma shrink-0" />
                  {activity.date}
                </span>
              )}
              {activity.location && (
                <span className="inline-flex items-center gap-2 bg-white px-5 py-2.5 rounded-full border border-slate-200 shadow-xs">
                  <MapPin className="w-4 h-4 text-michma shrink-0" />
                  {activity.location}
                </span>
              )}
            </div>
          </Reveal>
        )}

        {/* Quote / Key Summary */}
        {(activity.quote || activity.summary) && (
          <Reveal delay={0.3}>
            <blockquote className="mt-12 rounded-3xl bg-michma-soft/60 border border-michma/20 p-8 md:p-10 shadow-xs">
              <Quote className="w-8 h-8 text-michma mb-4" />
              <p className="font-heading text-xl lg:text-2xl tracking-tight leading-snug text-michma-ink italic">
                "{activity.quote || activity.summary}"
              </p>
            </blockquote>
          </Reveal>
        )}

        {/* Description Paragraphs */}
        {descriptionParagraphs.length > 0 && (
          <div className="mt-12 space-y-6">
            {descriptionParagraphs.map((paragraph, i) => (
              <Reveal key={i} delay={0.35 + i * 0.05}>
                <p className="text-lg lg:text-xl leading-relaxed text-slate-700">
                  {paragraph}
                </p>
              </Reveal>
            ))}
          </div>
        )}

        {/* Key Highlights / Tags */}
        {highlights.length > 0 && (
          <Reveal delay={0.4}>
            <div className="mt-16 pt-10 border-t border-slate-200">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500 font-bold mb-4 flex items-center gap-2">
                <Tag className="w-3.5 h-3.5 text-michma" /> Key Focus & Takeaways
              </p>
              <div className="flex flex-wrap gap-2.5">
                {highlights.map((item) => (
                  <span
                    key={item}
                    className="rounded-full border border-michma/30 bg-michma-soft text-michma px-4 py-2 text-sm font-semibold shadow-xs"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>
        )}
      </section>

      {/* Prev / Next Pagination Controls */}
      <section className="border-t border-slate-200/80 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 grid grid-cols-2 gap-6">
          <Link
            to={`/activity/${prev.slug}`}
            data-testid="prev-activity"
            className="group flex flex-col items-start hover:opacity-90 transition-opacity"
          >
            <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-slate-500 font-bold mb-2">
              <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1 text-michma" />
              Previous Activity
            </span>
            <span className="font-heading font-bold text-lg md:text-xl tracking-tight text-michma-ink group-hover:text-michma transition-colors line-clamp-1">
              {prev.title || prev.name}
            </span>
          </Link>

          <Link
            to={`/activity/${next.slug}`}
            data-testid="next-activity"
            className="group flex flex-col items-end text-right hover:opacity-90 transition-opacity"
          >
            <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-slate-500 font-bold mb-2">
              Next Activity
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1 text-michma" />
            </span>
            <span className="font-heading font-bold text-lg md:text-xl tracking-tight text-michma-ink group-hover:text-michma transition-colors line-clamp-1">
              {next.title || next.name}
            </span>
          </Link>
        </div>
      </section>
    </div>
  );
}